import {
  ERROR_CODES,
  isApiErrorBody,
  type ArticleFamily,
  type AuditEntry,
  type AuditQuery,
  type CoupleMotif,
  type ChangePasswordInput,
  type CreateFamilyInput,
  type CreateMovementTypeInput,
  type CreateUserInput,
  type DecideFamilyInput,
  type ImportBatch,
  type ImportType,
  type ListFamiliesQuery,
  type ListImportsQuery,
  type ListMovementTypesQuery,
  type ListProductsQuery,
  type CreateValidationInput,
  type FamilyEvidence,
  type CoherenceQueryInput,
  type ClosingQueryInput,
  type DashboardData,
  type HistoriqueQueryInput,
  type HistoriqueValidation,
  type PropositionRematch,
  type RecapitulatifTemporaires,
  type CoherenceRapport,
  type CommentType,
  type DiscrepancyComment,
  type DiscrepancyDrilldown,
  type DiscrepancyRow,
  type CreateRapprochementInput,
  type EntreeFil,
  type ListRestantQueryInput,
  type Rapprochement,
  type RapprochementCree,
  type ReconciliationType,
  type RestantPage,
  type ResumeSelectionVue,
  type EntreeChangelog,
  type MouvementsDuJour,
  type DiscrepancyValidation,
  type NeutralisationProposal,
  type KpiEntreesComparaison,
  type ListDiscrepanciesQueryInput,
  type ReconciliationQueryInput,
  type ReconciliationResult,
  type ListUsersQuery,
  type LoginInput,
  type MovementType,
  type Paginated,
  type Product,
  type DecideFamilyExtensionInput,
  type FamilyExtension,
  type FamilyStatus,
  type SessionUser,
  type UpdateFamilyInput,
  type UpdateMovementTypeInput,
  type UpdateProductGestionInput,
  type UpdateReconciliationTypeInput,
  type UpdateUserInput,
  type User,
} from '@kn/shared';

const API_BASE = '/api';

/** Bilan renvoye par la detection : jumeaux FAB 1:1 **et** grappes copacking. */
export interface FamilyDetectionSummary {
  fabricationsAnalysees: number;
  createdFamilyIds: string[];
  ignoredProposals: string[][];
  proposals: Array<{ codpros: string[]; references: string[] }>;
  skipped: Array<{ reference: string; reason: string; codpros: string[] }>;
  /** Grappes {composé + composants} declarees par les tables de copacking. */
  /** Familles FAB creees et activees sans relecture (regle 8 v11). */
  autoValideesFab: number;
  /** Celles restees PROPOSEE, sous le seuil. */
  proposeesFab: number;
  copacking: {
    liensAnalyses: number;
    createdFamilyIds: string[];
    createdExtensionIds: string[];
    extensionsAppliquees: number;
    grappes: Array<{ codpros: string[]; composes: string[] }>;
    extensions: Array<{ familyId: string; codprosAAjouter: string[]; composes: string[] }>;
    ignores: Array<{ compose: string; raison: string; codpros: string[] }>;
  };
}

/** Ce que la fiche affiche du fil de vie (regle 14 v11.2). */
export interface JournalEcart {
  entrees: EntreeFil[];
  /** Date a laquelle le suivi des evolutions a commence. */
  journalDemarreLe: string | null;
  /** Vrai si l'ecart est ne avant le journal : son fil n'a pas d'apparition. */
  anterieurAuJournal: boolean;
}

/** Comptes du rattrapage de la regle d'auto-validation sur le stock existant. */
export interface FamilyRattrapageSummary {
  grappesCreees: number;
  extensionsAppliquees: number;
  fabCreeesAutoValidees: number;
  grappesAnciennesValidees: number;
  fabAnciennesValidees: number;
  resteesProposees: number;
}

/** Erreur portant le code stable renvoye par l'API. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.code === ERROR_CODES.NOT_AUTHENTICATED;
  }

  get requiresPasswordChange(): boolean {
    return this.code === ERROR_CODES.PASSWORD_CHANGE_REQUIRED;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: 'same-origin',
      // Multipart : le navigateur doit poser lui-meme le Content-Type avec sa
      // frontiere. On ne force l'en-tete que pour les corps JSON.
      headers: typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      "Le serveur est injoignable. Verifiez qu'il est démarre.",
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiError(response.status, payload.code, payload.message, payload.details);
    }
    throw new ApiError(response.status, 'INTERNAL_ERROR', 'Une erreur inattendue est survenue.');
  }

  return payload as T;
}

function toQueryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** Chaîne de requête de l'historique des rapprochements, partagée liste et export. */
function filtreRapprochements(statut: string | undefined, types: readonly string[]): string {
  const params = new URLSearchParams();
  if (statut) params.set('statut', statut);
  if (types.length > 0) params.set('types', types.join(','));
  const chaine = params.toString();
  return chaine ? `?${chaine}` : '';
}

export const api = {
  /** Tableau de bord du pilotage : tout y est recalculé, rien n'est figé. */
  dashboard: (): Promise<DashboardData> => request('/dashboard'),

  login: (body: LoginInput): Promise<SessionUser> =>
    request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),

  logout: (): Promise<void> => request('/auth/logout', { method: 'POST' }),

  me: (): Promise<SessionUser> => request('/auth/me'),

  changePassword: (body: ChangePasswordInput): Promise<SessionUser> =>
    request('/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),

  listUsers: (query: Partial<ListUsersQuery> = {}): Promise<Paginated<User>> =>
    request(`/users${toQueryString(query)}`),

  createUser: (body: CreateUserInput): Promise<User> =>
    request('/users', { method: 'POST', body: JSON.stringify(body) }),

  updateUser: (id: string, body: UpdateUserInput): Promise<User> =>
    request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteUser: (id: string): Promise<void> => request(`/users/${id}`, { method: 'DELETE' }),

  listAudit: (query: Partial<AuditQuery> = {}): Promise<Paginated<AuditEntry>> =>
    request(`/audit${toQueryString(query)}`),

  // --- Referentiels ---

  listProducts: (query: Partial<ListProductsQuery> = {}): Promise<Paginated<Product>> =>
    request(`/products${toQueryString(query)}`),

  /** Regle metier 13 : seuls les attributs de gestion sont modifiables. */
  updateProductGestion: (id: string, body: UpdateProductGestionInput): Promise<Product> =>
    request(`/products/${id}/gestion`, { method: 'PATCH', body: JSON.stringify(body) }),

  listMovementTypes: (
    query: Partial<ListMovementTypesQuery> = {},
  ): Promise<Paginated<MovementType>> => request(`/movement-types${toQueryString(query)}`),

  createMovementType: (body: CreateMovementTypeInput): Promise<MovementType> =>
    request('/movement-types', { method: 'POST', body: JSON.stringify(body) }),

  updateMovementType: (id: string, body: UpdateMovementTypeInput): Promise<MovementType> =>
    request(`/movement-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  listFamilies: (query: Partial<ListFamiliesQuery> = {}): Promise<Paginated<ArticleFamily>> =>
    request(`/families${toQueryString(query)}`),

  createFamily: (body: CreateFamilyInput): Promise<ArticleFamily> =>
    request('/families', { method: 'POST', body: JSON.stringify(body) }),

  updateFamily: (id: string, body: UpdateFamilyInput): Promise<ArticleFamily> =>
    request(`/families/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  decideFamily: (id: string, body: DecideFamilyInput): Promise<ArticleFamily> =>
    request(`/families/${id}/decision`, { method: 'POST', body: JSON.stringify(body) }),

  deleteFamily: (id: string): Promise<void> => request(`/families/${id}`, { method: 'DELETE' }),

  /**
   * L'ecart designe par une adresse partageable `/ecarts/<segment>`.
   * L'adresse porte la cle suivie, pas l'identifiant de l'ecart : celui-ci
   * change des qu'un ecart est solde puis reapparait.
   */
  /** Version en service et notes qui l'accompagnent. */
  version: (): Promise<{ version: string; notes: EntreeChangelog[] }> => request('/version'),

  /** Mouvements d'une journee, charges a la demande depuis la fiche. */
  mouvementsDuJour: (id: string, jour: string): Promise<MouvementsDuJour> =>
    request(`/discrepancies/${id}/mouvements/${jour}`),

  ecartParAdresse: (segment: string): Promise<DiscrepancyRow> =>
    request(`/discrepancies/par-cle/${encodeURIComponent(segment)}`),

  /** Extensions proposees d'une famille existante (grappes copacking). */
  listFamilyExtensions: (statut?: FamilyStatus): Promise<FamilyExtension[]> =>
    request(`/families/extensions${statut ? `?statut=${statut}` : ''}`),

  decideFamilyExtension: (
    id: string,
    body: DecideFamilyExtensionInput,
  ): Promise<FamilyExtension> =>
    request(`/families/extensions/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Lance la detection : jumeaux FAB 1:1, puis grappes des tables copacking. */
  runFamilyDetection: (): Promise<FamilyDetectionSummary> =>
    request('/families/detection', { method: 'POST' }),

  runFamilyRattrapage: (): Promise<FamilyRattrapageSummary> =>
    request('/families/rattrapage', { method: 'POST' }),

  deactivateFamily: (id: string): Promise<ArticleFamily> =>
    request(`/families/${id}/desactivation`, { method: 'POST' }),

  // --- Rapprochement brut ---

  reconcile: (query: ReconciliationQueryInput = {}): Promise<ReconciliationResult> =>
    request(`/reconciliation${toQueryString(query as Record<string, unknown>)}`),

  /** KPI d'entrées, hors ODE et avec ODE, sur la même période que l'écran. */
  kpiEntrees: (query: ReconciliationQueryInput = {}): Promise<KpiEntreesComparaison> =>
    request(`/reconciliation/kpi-entrees${toQueryString(query as Record<string, unknown>)}`),

  /** URL de telechargement de l'export Excel, aux memes filtres que l'ecran. */
  reconciliationExportUrl: (query: ReconciliationQueryInput = {}): string =>
    `${API_BASE}/reconciliation/export${toQueryString(query as Record<string, unknown>)}`,

  // --- Imports ---

  /** Fabrications FAB qui fondent une proposition de famille (père → fils). */
  familyEvidence: (id: string): Promise<FamilyEvidence> => request(`/families/${id}/fabrications`),

  /** Cohérence FAB ↔ tables de copacking : contrôle informatif (règle 15). */
  coherenceCopacking: (query: CoherenceQueryInput = {}): Promise<CoherenceRapport> =>
    request(`/copacking/coherence${toQueryString(query as Record<string, unknown>)}`),

  coherenceExportUrl: (query: CoherenceQueryInput = {}): string =>
    `${API_BASE}/copacking/coherence/export${toQueryString(query as Record<string, unknown>)}`,

  // --- Écarts quotidiens ---

  listDiscrepancies: (query: ListDiscrepanciesQueryInput = {}): Promise<DiscrepancyRow[]> =>
    request(`/discrepancies${toQueryString(query as Record<string, unknown>)}`),

  /** Drill-down d'un écart : jour par jour, code par code, les deux côtés. */
  /** Fil de vie d'un ecart : evenements systeme, commentaires, validations. */
  journalEcart: (id: string): Promise<JournalEcart> => request(`/discrepancies/${id}/journal`),

  restant: (query: ListRestantQueryInput): Promise<RestantPage> =>
    request(`/restant${toQueryString(query)}`),

  /** Couples code + motif présents, avec leur libellé (filtre du Restant). */
  couplesMotifs: (): Promise<CoupleMotif[]> => request('/restant/motifs'),

  /** Aperçu d'une sélection ; le type choisi décide de la validation proposée. */
  resumeSelection: (mouvementIds: string[], typeId?: string): Promise<ResumeSelectionVue> =>
    request('/restant/resume', {
      method: 'POST',
      body: JSON.stringify({ mouvementIds, ...(typeId ? { typeId } : {}) }),
    }),

  reconciliationTypes: (actifsSeuls: boolean): Promise<ReconciliationType[]> =>
    request(`/reconciliation-types${actifsSeuls ? '?actifs=true' : ''}`),

  createReconciliationType: (body: { libelle: string }): Promise<ReconciliationType> =>
    request('/reconciliation-types', { method: 'POST', body: JSON.stringify(body) }),

  updateReconciliationType: (
    id: string,
    body: UpdateReconciliationTypeInput,
  ): Promise<ReconciliationType> =>
    request(`/reconciliation-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** Export Excel des rapprochements manuels, au même filtre que la liste. */
  rapprochementsExportUrl: (statut?: 'ACTIF' | 'ANNULE', types: string[] = []): string =>
    `/api/rapprochements/export${filtreRapprochements(statut, types)}`,

  rapprocher: (body: CreateRapprochementInput): Promise<RapprochementCree> =>
    request('/rapprochements', { method: 'POST', body: JSON.stringify(body) }),

  /** Historique des rapprochements : statut et types (OU), comme son export. */
  rapprochements: (statut?: 'ACTIF' | 'ANNULE', types: string[] = []): Promise<Rapprochement[]> =>
    request(`/rapprochements${filtreRapprochements(statut, types)}`),

  annulerRapprochement: (id: string, motif: string): Promise<Rapprochement> =>
    request(`/rapprochements/${id}/annulation`, {
      method: 'POST',
      body: JSON.stringify({ motif }),
    }),

  acquitterConflit: (id: string, motif: string): Promise<Rapprochement> =>
    request(`/rapprochements/${id}/acquittement`, {
      method: 'POST',
      body: JSON.stringify({ motif }),
    }),

  commentTypes: (actifsSeuls: boolean): Promise<CommentType[]> =>
    request(`/comment-types${actifsSeuls ? '?actifs=true' : ''}`),

  createCommentType: (body: { libelle: string }): Promise<CommentType> =>
    request('/comment-types', { method: 'POST', body: JSON.stringify(body) }),

  updateCommentType: (
    id: string,
    body: { libelle?: string; actif?: boolean },
  ): Promise<CommentType> =>
    request(`/comment-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  commentaires: (segment: string): Promise<DiscrepancyComment[]> =>
    request(`/discrepancies/par-cle/${encodeURIComponent(segment)}/commentaires`),

  ajouterCommentaire: (
    segment: string,
    body: { typeId: string; texte: string },
  ): Promise<DiscrepancyComment> =>
    request(`/discrepancies/par-cle/${encodeURIComponent(segment)}/commentaires`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  discrepancyDrilldown: (id: string): Promise<DiscrepancyDrilldown> =>
    request(`/discrepancies/${id}/drilldown`),

  /** URL de téléchargement du drill-down au format Excel. */
  discrepancyDrilldownExportUrl: (id: string): string =>
    `${API_BASE}/discrepancies/${id}/drilldown/export`,

  /** Proposition de neutralisation assistée : proposée, jamais appliquée. */
  neutralisationProposal: (id: string): Promise<NeutralisationProposal | null> =>
    request(`/discrepancies/${id}/proposition`),

  validerEcart: (id: string, data: CreateValidationInput): Promise<DiscrepancyValidation> =>
    request(`/discrepancies/${id}/validations`, { method: 'POST', body: JSON.stringify(data) }),

  annulerValidation: (
    validationId: string,
    motifAnnulation: string,
  ): Promise<DiscrepancyValidation> =>
    request(`/discrepancies/validations/${validationId}/annulation`, {
      method: 'POST',
      body: JSON.stringify({ motifAnnulation }),
    }),

  /** URL de téléchargement de l'état des écarts, aux mêmes filtres que l'écran. */
  discrepanciesExportUrl: (query: ListDiscrepanciesQueryInput = {}): string =>
    `${API_BASE}/discrepancies/export${toQueryString(query as Record<string, unknown>)}`,

  // --- Clôture de fin d'année ---

  /** Ce qui a été expliqué sans être résorbé : la base des ODE annuels. */
  recapitulatifTemporaires: (
    query: ClosingQueryInput = {},
  ): Promise<RecapitulatifTemporaires & { du: string; au: string }> =>
    request(`/discrepancies/cloture/recapitulatif${toQueryString(query as Record<string, unknown>)}`),

  recapitulatifExportUrl: (query: ClosingQueryInput = {}): string =>
    `${API_BASE}/discrepancies/cloture/recapitulatif/export${toQueryString(query as Record<string, unknown>)}`,

  /** Propositions de bascule vers DÉFINITIVE : proposées, jamais appliquées. */
  propositionsRematch: (query: ClosingQueryInput = {}): Promise<PropositionRematch[]> =>
    request(`/discrepancies/cloture/rematch${toQueryString(query as Record<string, unknown>)}`),

  historiqueValidations: (query: HistoriqueQueryInput = {}): Promise<HistoriqueValidation[]> =>
    request(
      `/discrepancies/validations/historique${toQueryString(query as Record<string, unknown>)}`,
    ),

  historiqueExportUrl: (query: HistoriqueQueryInput = {}): string =>
    `${API_BASE}/discrepancies/validations/historique/export${toQueryString(query as Record<string, unknown>)}`,

  /** Confirme un rattrapage suspendu par le garde-fou (règle 2). */
  confirmerRattrapage: (id: string): Promise<ImportBatch> =>
    request(`/imports/${id}/confirmer-rattrapage`, { method: 'POST' }),

  listImports: (query: Partial<ListImportsQuery> = {}): Promise<Paginated<ImportBatch>> =>
    request(`/imports${toQueryString(query)}`),

  /** Depot multipart. Le navigateur pose lui-meme le Content-Type et sa frontiere. */
  uploadImport: (
    type: ImportType,
    file: File,
    champs: Record<string, string> = {},
  ): Promise<ImportBatch> => {
    const formulaire = new FormData();
    formulaire.append('file', file);
    for (const [cle, valeur] of Object.entries(champs)) {
      formulaire.append(cle, valeur);
    }
    return request(`/imports/${type}`, { method: 'POST', body: formulaire });
  },
};
